// El calendario de Instagram —
// ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts y
// ADR-2026-10-06-el-calendario-se-ve-como-instagram.
//
// Arriba, el agente (la forma principal de llenarlo); abajo, el mes. Al abrir un post la
// pantalla pasa a ser sólo el editor, con su vista previa: el mes se esconde hasta volver
// (ADR-2026-10-06-el-post-se-edita-solo, que supersede a
// ADR-2026-09-15-el-post-se-edita-al-lado-del-calendario).
//
// La pantalla se registra en el puente del chat con sus herramientas: mientras está
// montada, el agente puede leer el plan, proponer horarios y crear o mover posts
// (ADR-2026-10-06-el-agente-planifica-el-calendario).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, Check, ChevronLeft, ChevronRight, Link2, List, Loader2, Plus, Share2, Sparkles } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import { useRegistrarPantalla } from '../../../chat-atlas';
import { boton } from '../botones';
import {
  DIAS_DE_LA_SEMANA,
  correrMes,
  grillaDelMes,
  horaDe,
  mesDe,
  tituloDelMes,
  type DiaDelMes,
} from './calendario';
import { comoSeLee, sugerirMomento, type PostEnAgenda } from './mejorMomento';
import { EditorDePost } from './EditorDePost';
import { PostPublicado } from './PostPublicado';
import { useUserGroup } from '../../../hooks/useUserGroup';
import { seVenLasMetricas } from './avisos';
import { PlanificarConElAgente } from './PlanificarConElAgente';
import { CalendarioMovil } from './CalendarioMovil';
import { IconoDeFormato, Miniatura, TONOS, tonoDe, type Tono } from './estadoDelPost';
import {
  crearPost,
  estadosDePosts,
  fetchAgendaDeLaEmpresa,
  fetchPosts,
  guardarPost,
  type PostConMiniaturas,
} from './postsApi';
import { crearLinkCompartido, revocarLink, urlDeCalendarioCompartido } from './compartirApi';
import { useRecorrido } from '../recorridos';
import { pasosDelCalendario } from './recorridoDelCalendario';
import { ejecutarHerramientaDelCalendario } from './agente/ejecutar';
import {
  CLAVE_PANTALLA_CALENDARIO,
  HERRAMIENTAS_DEL_CALENDARIO,
  SKILL_DEL_CALENDARIO,
} from './agente/herramientas';

/** Cuántos posts se ven en una casilla antes de resumir el resto. */
const POSTS_VISIBLES = 3;

/** El tipo de dato que viaja al arrastrar un post a otro día. */
const ARRASTRE = 'application/x-atlas-post';

type Vista = 'mes' | 'lista';

/**
 * Los posts que armó el agente, para marcarlos en la grilla. Viven en la pestaña y no en
 * la base: es una ayuda para revisar lo recién armado, no un dato del post.
 */
function useDelAgente(lanzamientoId: string) {
  const clave = `atlas:calendario:del-agente:${lanzamientoId}`;
  const [ids, setIds] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(clave) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const marcar = useCallback(
    (nuevos: string[]) =>
      setIds((antes) => {
        const s = new Set([...antes, ...nuevos]);
        try {
          sessionStorage.setItem(clave, JSON.stringify([...s]));
        } catch {
          // Sin storage la marca dura lo que la pantalla abierta: alcanza.
        }
        return s;
      }),
    [clave],
  );
  return [ids, marcar] as const;
}

/** Cada cuánto se mira el estado de los posts que están por salir, y cuánto antes y después de su hora. */
const CADA_MS_ATENTO = 15_000;
const VENTANA_ATENTA_MS = 30 * 60_000;

export function CalendarioDeRedes({
  lanzamientoId,
  companyId,
  fechaLanzamiento,
  aceptaVideo,
  onCambio,
  onEditando,
  postInicial = null,
}: {
  lanzamientoId: string;
  companyId: string;
  /** `YYYY-MM-DD` de la fecha del lanzamiento, si tiene. */
  fechaLanzamiento: string | null;
  aceptaVideo: boolean;
  /** Para que el encabezado recuente. */
  onCambio: () => void;
  /** Hay un post abierto: la página esconde su encabezado y deja el del editor. */
  onEditando?: (editando: boolean) => void;
  /** Un post para abrir al entrar (el "Abrir en el calendario" de las métricas). */
  postInicial?: string | null;
}) {
  const [mes, setMes] = useState(() => mesDe(new Date()));
  const [posts, setPosts] = useState<PostConMiniaturas[]>([]);
  const [agenda, setAgenda] = useState<PostEnAgenda[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState<string | null>(postInicial);
  const abiertoRef = useRef(abierto);
  abiertoRef.current = abierto;
  /** Cómo salir del post abierto pasando por su pregunta de guardar (lo registra el editor). */
  const salidaDelEditor = useRef<((seguir: () => void) => void) | null>(null);
  const registrarSalida = useCallback((salir: ((seguir: () => void) => void) | null) => {
    salidaDelEditor.current = salir;
  }, []);
  const [aviso, setAviso] = useState<string | null>(null);
  /** El post que se creó con "Nuevo post" y todavía no se guardó: descartarlo lo borra. */
  const [nuevoId, setNuevoId] = useState<string | null>(null);
  /** "Post guardado…": la confirmación de que quedó en el calendario. */
  const [hecho, setHecho] = useState<string | null>(null);
  useEffect(() => {
    if (!hecho) return;
    const t = window.setTimeout(() => setHecho(null), 4500);
    return () => window.clearTimeout(t);
  }, [hecho]);
  // Mes o lista es de escritorio: el teléfono tiene su propia forma (`CalendarioMovil`).
  const [vista, setVista] = useState<Vista>('mes');
  const [delAgente, marcarDelAgente] = useDelAgente(lanzamientoId);
  const [modalCompartir, setModalCompartir] = useState(false);
  const [linkCompartido, setLinkCompartido] = useState<string | null>(null);
  const [compartiendoLink, setCompartiendoLink] = useState(false);

  /**
   * `onCambio` vive en una ref y NO en las dependencias de nada: el padre lo pasa nuevo
   * en cada pintado y, si entrara, la lectura se dispararía a sí misma para siempre
   * (ADR-2026-09-15-el-calendario-se-recargaba-a-si-mismo).
   */
  const avisarRef = useRef(onCambio);
  avisarRef.current = onCambio;

  const desde = useMemo(() => {
    if (!fechaLanzamiento) return null;
    const [a, m, d] = fechaLanzamiento.split('-').map(Number);
    return a && m && d ? new Date(a, m - 1, d) : null;
  }, [fechaLanzamiento]);

  /** El último pedido de lectura: uno viejo que llega tarde (otro calendario) no pisa. */
  const pedidoRef = useRef(0);

  /** Leer. No avisa nada hacia arriba: leer no es un cambio. */
  const recargar = useCallback(async () => {
    const n = ++pedidoRef.current;
    const [p, a] = await Promise.all([
      fetchPosts(lanzamientoId, companyId),
      fetchAgendaDeLaEmpresa(companyId),
    ]);
    if (n !== pedidoRef.current) return;
    setPosts(p.posts);
    setAgenda(a.agenda);
    setAviso(p.error ?? a.error);
    setCargando(false);
  }, [lanzamientoId, companyId]);

  /** Leer y avisar. Va después de ESCRIBIR, que es cuando los contadores se mueven. */
  const recargarYAvisar = useCallback(async () => {
    await recargar();
    avisarRef.current();
  }, [recargar]);

  useEffect(() => {
    void recargar();
  }, [lanzamientoId, companyId, recargar]);

  // Mientras un post está saliendo (o le llega la hora), el backend lo publica sin avisarle a esta pantalla:
  // cada 15 s se mira SÓLO el estado de esos posts y, si alguno cambió, se recarga todo (con miniaturas).
  // Sin esto quedaba "Publicando…" o "pendiente" hasta recargar (review Greptile #225).
  const atentos = useMemo(() => {
    const ahora = Date.now();
    return posts
      .filter(
        (p) =>
          p.estado === 'publicando' ||
          (p.estado === 'pendiente' && p.cuando && Math.abs(p.cuando.getTime() - ahora) < VENTANA_ATENTA_MS),
      )
      .map((p) => [p.id, p.estado] as const);
  }, [posts]);
  useEffect(() => {
    if (atentos.length === 0) return;
    const antes = new Map(atentos);
    const t = window.setInterval(async () => {
      const ahora = await estadosDePosts([...antes.keys()]);
      if (ahora && [...antes].some(([id, e]) => ahora.get(id) !== e)) void recargar();
    }, CADA_MS_ATENTO);
    return () => window.clearInterval(t);
  }, [atentos, recargar]);

  const grilla = useMemo(() => grillaDelMes(mes, posts, new Date()), [mes, posts]);
  const abiertoEl = posts.find((p) => p.id === abierto) ?? null;
  const { isDevTester } = useUserGroup();
  const conMetricas = seVenLasMetricas(isDevTester);

  // El post que vino de las métricas: el mes de atrás es el suyo, así al cerrarlo se lo ve en su día.
  const inicialUsado = useRef(false);
  useEffect(() => {
    if (!postInicial || inicialUsado.current) return;
    const p = posts.find((x) => x.id === postInicial);
    if (!p) return;
    inicialUsado.current = true;
    if (p.cuando) setMes(mesDe(p.cuando));
  }, [posts, postInicial]);

  /** Lo del mes que se está mirando: alimenta el resumen y la lista. */
  const delMes = useMemo(
    () =>
      posts
        .filter(
          (p) =>
            p.cuando &&
            p.cuando.getFullYear() === mes.getFullYear() &&
            p.cuando.getMonth() === mes.getMonth(),
        )
        .sort((a, b) => a.cuando!.getTime() - b.cuando!.getTime()),
    [posts, mes],
  );
  const resumen = useMemo(() => {
    const ahora = Date.now();
    const cuenta = { listo: 0, falta: 0, publicado: 0, error: 0 } as Record<Tono, number>;
    for (const p of delMes) cuenta[tonoDe(p)] += 1;
    const proximo =
      posts.find((p) => p.cuando && p.cuando.getTime() > ahora && p.estado === 'pendiente') ?? null;
    const incompletos = posts.filter((p) => p.estado === 'pendiente' && tonoDe(p) === 'falta').length;
    return { cuenta, proximo, incompletos };
  }, [delMes, posts]);

  const compartir = async () => {
    setCompartiendoLink(true);
    const r = await crearLinkCompartido(lanzamientoId, companyId);
    setCompartiendoLink(false);
    if (r.error || !r.token) {
      setAviso(r.error);
      return;
    }
    setLinkCompartido(urlDeCalendarioCompartido(r.token));
    setModalCompartir(true);
  };

  /** Tocar un día vacío: el día lo eligió el usuario, la hora la elegimos nosotros. */
  const nuevoEn = async (dia: Date | null) => {
    const acotado = dia
      ? {
          desde: dia,
          hasta: new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), 23, 59),
        }
      : { desde };
    const s = sugerirMomento({ agenda, ahora: new Date(), ...acotado });
    const r = await crearPost(lanzamientoId, companyId, s.cuando);
    if (r.error || !r.id) return setAviso(r.error);
    await recargarYAvisar();
    setNuevoId(r.id);
    setAbierto(r.id);
  };

  /**
   * Arrastrar un post a otro día: cambia el día y conserva la hora (Later, Buffer). Se
   * mueve en pantalla al toque y después se guarda; si falla, la relectura lo devuelve.
   */
  const mover = async (id: string, dia: Date) => {
    const p = posts.find((x) => x.id === id);
    // Lo que ya salió, o está saliendo, no se mueve (ADR-2026-10-28-instagram-publica-el-calendario).
    if (!p || p.estado === 'publicado' || p.estado === 'publicando') return;
    const base = p.cuando ?? new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), 12, 0);
    const nuevo = new Date(
      dia.getFullYear(),
      dia.getMonth(),
      dia.getDate(),
      base.getHours(),
      base.getMinutes(),
    );
    if (p.cuando && nuevo.getTime() === p.cuando.getTime()) return;
    if (nuevo.getTime() < Date.now()) return setAviso('Ese horario ya pasó: elegí un día que venga.');
    setPosts((ps) => ps.map((x) => (x.id === id ? { ...x, cuando: nuevo } : x)));
    const r = await guardarPost(id, { cuando: nuevo });
    if (r.error) setAviso(r.error);
    await recargarYAvisar();
  };

  // ── El agente ve y toca esta pantalla ───────────────────────────────────
  useRegistrarPantalla({
    clave: CLAVE_PANTALLA_CALENDARIO,
    describir: () => ({
      pantalla: 'el calendario de Instagram',
      ruta: '/instagram',
      queHace: 'Planifica los posts de Instagram de la marca: piezas, descripción y cuándo sale cada uno.',
      campos: [],
      faltaParaGenerar: [],
      costo: 'Planificar no cuesta créditos.',
      estado: [
        `Mirando ${tituloDelMes(mes)} en vista ${vista}.`,
        `${posts.length} post${posts.length === 1 ? '' : 's'} en el plan; este mes ${delMes.length} (${resumen.cuenta.listo} listos, ${resumen.cuenta.falta} a completar, ${resumen.cuenta.publicado} publicados).`,
        resumen.proximo?.cuando ? `Próximo: ${comoSeLee(resumen.proximo.cuando)}.` : 'No hay posts próximos.',
        abiertoEl ? `Abierto en el editor: post_id=${abiertoEl.id}.` : '',
      ]
        .filter(Boolean)
        .join(' '),
    }),
    aplicar: () => ({ aplicados: [], fallidos: [] }),
    herramientas: HERRAMIENTAS_DEL_CALENDARIO,
    ejecutar: (nombre, input) =>
      ejecutarHerramientaDelCalendario(nombre, (input ?? {}) as Record<string, unknown>, {
        lanzamientoId,
        companyId,
        recargar: recargarYAvisar,
        abrirPost: (id, cuando) => {
          const abrir = () => {
            if (cuando) setMes(mesDe(cuando));
            setAbierto(id);
          };
          // Con otro post abierto, el cambio pasa por la pregunta de salida de ese editor:
          // cambiar el `key` directo tiraba su borrador sin preguntar (review Greptile #169).
          if (abiertoRef.current && abiertoRef.current !== id && salidaDelEditor.current) {
            salidaDelEditor.current(abrir);
            return 'esperando';
          }
          abrir();
          return 'abierto';
        },
        mostrarMes: (d) => setMes(mesDe(d)),
        marcarDelAgente,
      }),
    skill: SKILL_DEL_CALENDARIO,
  });

  const editando = !!abiertoEl;
  const editandoRef = useRef(onEditando);
  editandoRef.current = onEditando;
  useEffect(() => {
    editandoRef.current?.(editando);
  }, [editando]);

  // La primera vez en el calendario, los cartelitos del mes. Con el mes a la vista.
  useRecorrido('instagram_tour_calendario', !cargando && !editando, pasosDelCalendario);

  if (cargando) {
    return (
      <div className="mt-8 flex items-center justify-center gap-2 py-16 text-sm text-neutral-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando el calendario…
      </div>
    );
  }

  // Un post abierto ocupa la pantalla: el editor con su vista previa, sin el mes al costado
  // (ADR-2026-10-06-el-post-se-edita-solo). El volver de arriba devuelve al calendario.
  // La `key` reinicia la animación de entrada al pasar de un post a otro.
  // Un post que ya salió no se edita (Instagram no deja): se mira, con cómo le fue
  // (ADR-2026-10-30-el-post-publicado-se-mira-no-se-edita).
  if (abiertoEl && abiertoEl.estado === 'publicado' && conMetricas) {
    return (
      <div key={abiertoEl.id} className="animate-ficha-entra">
        <PostPublicado post={abiertoEl} companyId={companyId} onCerrar={() => setAbierto(null)} />
      </div>
    );
  }
  if (abiertoEl) {
    return (
      <div key={abiertoEl.id} className="animate-ficha-entra">
        <EditorDePost
          post={abiertoEl}
          companyId={companyId}
          agenda={agenda}
          desde={desde}
          aceptaVideo={aceptaVideo}
          delAgente={delAgente.has(abiertoEl.id)}
          esNuevo={abiertoEl.id === nuevoId}
          onCambio={() => void recargarYAvisar()}
          onGuardado={(cuando) => {
            setNuevoId(null);
            setMes(mesDe(cuando));
            setHecho(`Post guardado · sale el ${comoSeLee(cuando)}`);
          }}
          onCerrar={() => setAbierto(null)}
          registrarSalida={registrarSalida}
        />
      </div>
    );
  }

  return (
    <div className="mt-5">
      {hecho && (
        <p
          role="status"
          className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 py-3 px-4 text-sm font-medium text-emerald-800 animate-ficha-entra"
        >
          <Check className="w-4 h-4 shrink-0" /> {hecho}
        </p>
      )}
      {/* El agente: arriba en el teléfono; en la compu, debajo del calendario
          (ADR-2026-10-22-la-tarjeta-del-agente-va-debajo-del-calendario). */}
      <div data-tour="ig-agente" className="md:hidden">
        <PlanificarConElAgente
          version="telefono"
          mesVisible={mes.toLocaleDateString('es-AR', { month: 'long' })}
          incompletos={resumen.incompletos}
          vacio={posts.length === 0}
        />
      </div>
      {/* Los números en tarjetas son de escritorio: en el teléfono son los chips que filtran. */}
      <div data-tour="ig-resumen" className="hidden md:block">
        <ResumenDelMes
          total={delMes.length}
          cuenta={resumen.cuenta}
          proximo={resumen.proximo}
          onAbrir={setAbierto}
        />
      </div>

      {aviso && (
        <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 py-3 px-4 text-sm text-amber-900">
          {aviso}
        </p>
      )}

      <CalendarioMovil
        posts={posts}
        mes={mes}
        onCambiarMes={setMes}
        delAgente={delAgente}
        onAbrir={setAbierto}
        onNuevo={(d) => void nuevoEn(d)}
      />

      {/* ── Escritorio ── */}
      <div className="hidden md:block">
        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <h2 className="truncate text-lg font-semibold capitalize text-brand-black">
              {tituloDelMes(mes)}
            </h2>
            <button
              type="button"
              onClick={() => setMes((m) => correrMes(m, -1))}
              aria-label="Mes anterior"
              className="ml-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setMes((m) => correrMes(m, 1))}
              aria-label="Mes siguiente"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setMes(mesDe(new Date()))}
              className="ml-1 rounded-lg bg-neutral-100 py-1.5 px-3 text-xs font-medium text-brand-black hover:bg-neutral-200"
            >
              Hoy
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div data-tour="ig-vista">
              <SelectorDeVista vista={vista} onCambiar={setVista} />
            </div>
            <button
              type="button"
              onClick={() => void compartir()}
              disabled={compartiendoLink}
              aria-label="Compartir calendario"
              className={boton('secundario', { extra: 'shrink-0 ring-1 ring-[rgb(var(--brand-primary-rgb)_/_0.3)]' })}
            >
              {compartiendoLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Compartir
            </button>
            <button
              type="button"
              onClick={() => void nuevoEn(null)}
              data-tour="ig-nuevo"
              aria-label="Nuevo post"
              className={boton('primario', { extra: 'shrink-0' })}
            >
              <Plus className="w-4 h-4" />
              Nuevo post
            </button>
          </div>
        </div>

        {vista === 'lista' ? (
          <ListaDelMes
            posts={delMes}
            delAgente={delAgente}
            onAbrir={setAbierto}
            onNuevo={() => void nuevoEn(null)}
          />
        ) : (
          <>
            <div
              data-tour="ig-grilla"
              className="mt-3 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
            >
              <div className="grid grid-cols-7 border-b border-neutral-200 bg-neutral-50">
                {DIAS_DE_LA_SEMANA.map((d, i) => (
                  <div
                    key={d}
                    className={`py-2.5 px-1 text-center text-[11px] font-semibold uppercase tracking-wide ${
                      i >= 5 ? 'text-neutral-400' : 'text-neutral-500'
                    }`}
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {grilla.map((dia) => (
                  <Casilla
                    key={dia.fecha.toISOString()}
                    dia={dia}
                    agenda={agenda}
                    delAgente={delAgente}
                    onNuevo={() => void nuevoEn(dia.fecha)}
                    onAbrir={setAbierto}
                    onSoltar={(id) => void mover(id, dia.fecha)}
                  />
                ))}
              </div>
            </div>
            <Leyenda />
          </>
        )}

        <div data-tour="ig-agente-escritorio" className="mt-6">
          <PlanificarConElAgente
            version="escritorio"
            mesVisible={mes.toLocaleDateString('es-AR', { month: 'long' })}
            incompletos={resumen.incompletos}
            vacio={posts.length === 0}
          />
        </div>
      </div>

      {modalCompartir && linkCompartido && (
        <ModalCompartir
          url={linkCompartido}
          onCerrar={() => setModalCompartir(false)}
          onRevocar={async () => {
            const { error: err } = await revocarLink(lanzamientoId, companyId);
            if (err) { setAviso(err); return; }  // no cierra si falla
            setLinkCompartido(null);              // link revocado
            setModalCompartir(false);             // cierra el popup
          }}
        />
      )}
    </div>
  );
}

// ── Piezas de la pantalla ──────────────────────────────────────────────────

function SelectorDeVista({ vista, onCambiar }: { vista: Vista; onCambiar: (v: Vista) => void }) {
  const opcion = (v: Vista, etiqueta: string, icono: JSX.Element) => (
    <button
      type="button"
      onClick={() => onCambiar(v)}
      aria-pressed={vista === v}
      className={`flex items-center gap-1.5 rounded-lg py-1.5 px-2.5 text-xs font-medium transition-colors ${
        vista === v ? 'bg-white text-brand-black shadow-sm' : 'text-neutral-500 hover:text-brand-black'
      }`}
    >
      {icono}
      {etiqueta}
    </button>
  );
  return (
    <div className="flex rounded-xl bg-neutral-100 p-1">
      {opcion('mes', 'Mes', <CalendarDays className="w-3.5 h-3.5" />)}
      {opcion('lista', 'Lista', <List className="w-3.5 h-3.5" />)}
    </div>
  );
}

/** Cuatro números del mes que se mira, y el próximo post que sale. */
function ResumenDelMes({
  total,
  cuenta,
  proximo,
  onAbrir,
}: {
  total: number;
  cuenta: Record<Tono, number>;
  proximo: PostConMiniaturas | null;
  onAbrir: (id: string) => void;
}) {
  const dato = (valor: number, etiqueta: string, punto?: string) => (
    <div className="rounded-2xl border border-neutral-200 bg-white py-3 px-4 shadow-sm">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
        {punto && <span className={`h-1.5 w-1.5 rounded-full ${punto}`} />}
        {etiqueta}
      </p>
      <p className="mt-1 text-xl font-semibold text-brand-black">{valor}</p>
    </div>
  );
  return (
    <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
      {dato(total, 'Este mes')}
      {dato(cuenta.listo, 'Listos', TONOS.listo.punto)}
      {dato(cuenta.falta, 'A completar', TONOS.falta.punto)}
      {dato(cuenta.publicado, 'Publicados', TONOS.publicado.punto)}
      <button
        type="button"
        disabled={!proximo}
        onClick={() => proximo && onAbrir(proximo.id)}
        className="col-span-2 flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white py-3 px-4 text-left shadow-sm transition-colors enabled:hover:border-[rgb(var(--brand-primary-rgb)_/_0.225)] lg:col-span-1"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-xl bg-neutral-100">
          {proximo?.miniaturas[0] ? (
            <CachedImg src={proximo.miniaturas[0]} alt="" className="h-full w-full object-cover" />
          ) : (
            <CalendarDays className="w-4 h-4 text-neutral-400" />
          )}
        </span>
        <span className="min-w-0">
          <span className="block text-[11px] font-medium uppercase tracking-wide text-neutral-500">
            Próximo
          </span>
          <span className="block truncate text-sm font-semibold text-brand-black first-letter:uppercase">
            {proximo?.cuando
              ? `${proximo.cuando.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric' })} · ${horaDe(proximo)}`
              : 'Sin posts'}
          </span>
        </span>
      </button>
    </div>
  );
}

function Leyenda() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-500">
      {(['listo', 'falta', 'publicado', 'error'] as const).map((t) => (
        <span key={t} className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${TONOS[t].punto}`} />
          {TONOS[t].texto}
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-brand-primary" /> Lo armó el asistente
      </span>
      <span className="hidden md:inline">· Arrastrá un post a otro día para moverlo</span>
    </div>
  );
}

/** Popup para compartir el calendario con un link público. */
function ModalCompartir({
  url,
  onCerrar,
  onRevocar,
}: {
  url: string;
  onCerrar: () => void;
  onRevocar: () => Promise<void>;
}) {
  const [copiado, setCopiado] = useState(false);
  const [revocando, setRevocando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.select();
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', alTeclado);
    return () => window.removeEventListener('keydown', alTeclado);
  }, [onCerrar]);

  const copiar = async () => {
    await navigator.clipboard.writeText(url);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onCerrar}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md animate-ficha-entra overflow-hidden rounded-3xl bg-white shadow-2xl"
      >
        {/* Cabecera visual */}
        <div className="relative bg-gradient-to-br from-brand-primary via-brand-primary-claro to-[color:color-mix(in_srgb,var(--brand-primary)_55%,white)] px-6 pt-8 pb-10">
          {/* Patrón decorativo */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10" />
            <div className="absolute -left-4 top-8 h-20 w-20 rounded-full bg-white/5" />
            <div className="absolute right-12 bottom-2 h-14 w-14 rounded-full bg-white/10" />
          </div>
          <div className="relative">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm shadow-lg shadow-black/10">
              <Share2 className="w-6 h-6 text-white" />
            </div>
            <h2 className="mt-4 text-center text-xl font-bold text-white">
              Compartí tu calendario
            </h2>
            <p className="mt-1.5 text-center text-sm leading-relaxed text-white/80">
              Cualquier persona con este link puede ver tu calendario y dejar comentarios. No van a poder editar nada.
            </p>
          </div>
        </div>

        <div className="px-6 pb-6 pt-5">

        {/* El link */}
        <div className="mt-5 flex items-center gap-2 rounded-2xl border border-neutral-200 bg-neutral-50 p-1.5">
          <input
            ref={inputRef}
            type="text"
            readOnly
            value={url}
            className="min-w-0 flex-1 rounded-xl bg-transparent py-2 px-3 text-sm text-brand-black outline-none selection:bg-[rgb(var(--brand-primary-rgb)_/_0.225)]"
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <button
            type="button"
            onClick={() => void copiar()}
            className={`flex shrink-0 items-center gap-1.5 rounded-xl py-2.5 px-4 text-sm font-medium transition-colors ${
              copiado
                ? 'bg-emerald-500 text-white'
                : 'bg-brand-primary text-white hover:bg-brand-primary-hover'
            }`}
          >
            {copiado ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
            {copiado ? 'Copiado' : 'Copiar link'}
          </button>
        </div>

        {/* Qué puede hacer el visitante */}
        <div className="mt-5">
          <p className="mb-2.5 text-sm font-semibold text-brand-black">
            Qué puede hacer quien reciba el link
          </p>
          <div className="space-y-2 rounded-2xl border border-neutral-100 bg-neutral-50 p-3">
            {[
              { icono: '👁', texto: 'Ver el calendario y cada post planificado' },
              { icono: '💬', texto: 'Dejar comentarios en cualquier publicación' },
              { icono: '🔒', texto: 'No puede editar, borrar ni mover posts' },
            ].map((item) => (
              <div key={item.texto} className="flex items-center gap-3 rounded-xl bg-white py-2.5 px-3 shadow-sm">
                <span className="text-base">{item.icono}</span>
                <span className="text-sm text-neutral-700">{item.texto}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={async () => {
              setRevocando(true);
              await onRevocar();
              setRevocando(false);
            }}
            disabled={revocando}
            className="flex-1 rounded-xl bg-red-50 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
          >
            {revocando ? 'Revocando…' : 'Dejar de compartir'}
          </button>
          <button
            type="button"
            onClick={onCerrar}
            className="flex-1 rounded-xl bg-neutral-100 py-3 text-sm font-medium text-brand-black transition-colors hover:bg-neutral-200"
          >
            Cerrar
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

/** Un post en una casilla: hora, portada, formato y estado (Buffer). */
function Pildora({
  post,
  delAgente,
  onAbrir,
}: {
  post: PostConMiniaturas;
  delAgente: boolean;
  onAbrir: () => void;
}) {
  const tono = TONOS[tonoDe(post)];
  const arrastrable = post.estado !== 'publicado' && post.estado !== 'publicando';
  return (
    <button
      type="button"
      draggable={arrastrable}
      onDragStart={(e) => {
        e.dataTransfer.setData(ARRASTRE, post.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onAbrir}
      title={`${horaDe(post)} · ${tono.texto}${delAgente ? ' · lo armó el asistente' : ''}`}
      className={`flex w-full items-center gap-1.5 rounded-lg border-l-[3px] py-1 pr-1.5 pl-1 text-left transition-colors ${tono.pildora} ${arrastrable ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <span className="relative grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-md bg-white">
        <Miniatura post={post} />
      </span>
      <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-brand-black">
        {horaDe(post)}
      </span>
      <IconoDeFormato post={post} className="w-3 h-3 shrink-0 text-neutral-500" />
      {delAgente && <Sparkles aria-label="Lo armó el asistente" className="w-3 h-3 shrink-0 text-brand-primary" />}
    </button>
  );
}

function Casilla({
  dia,
  agenda,
  delAgente,
  onNuevo,
  onAbrir,
  onSoltar,
}: {
  dia: DiaDelMes<PostConMiniaturas>;
  agenda: PostEnAgenda[];
  delAgente: Set<string>;
  onNuevo: () => void;
  onAbrir: (id: string) => void;
  onSoltar: (postId: string) => void;
}) {
  /**
   * Un día con muchos posts se despliega en el lugar
   * (ADR-2026-09-16-un-dia-cargado-se-despliega-en-el-lugar): un índice fijo dejaba
   * posts sin forma de abrirlos.
   */
  const [desplegado, setDesplegado] = useState(false);
  const [encima, setEncima] = useState(false);
  const [sugerida, setSugerida] = useState<string | null>(null);

  // Al cambiar de mes se repliega solo: dejarlo abierto desacomoda la fila entera.
  useEffect(() => {
    setDesplegado(false);
  }, [dia.fecha.getMonth()]);

  // La agenda cambió (se creó o movió un post): la hora sugerida se vuelve a calcular.
  useEffect(() => {
    setSugerida(null);
  }, [agenda]);

  const visibles = desplegado ? dia.posts : dia.posts.slice(0, POSTS_VISIBLES);
  const resto = dia.posts.length - visibles.length;
  const finDeSemana = dia.fecha.getDay() === 0 || dia.fecha.getDay() === 6;
  const aceptaSoltar = !dia.esPasado || dia.esHoy;

  /** El horario que se le propondría a un post nuevo este día (Hootsuite). Al pasar el
   *  mouse y no antes: son 42 casillas y casi nadie mira todas. */
  const calcularSugerida = () => {
    if (sugerida || dia.esPasado || dia.posts.length > 0) return;
    const s = sugerirMomento({
      agenda,
      ahora: new Date(),
      desde: dia.fecha,
      hasta: new Date(dia.fecha.getFullYear(), dia.fecha.getMonth(), dia.fecha.getDate(), 23, 59),
    });
    if (s.cuando.toDateString() === dia.fecha.toDateString()) setSugerida(horaDe({ cuando: s.cuando }));
  };

  const numero = (
    <span
      className={`grid h-7 w-7 place-items-center rounded-full text-xs ${
        dia.esHoy
          ? 'bg-brand-primary font-semibold text-white'
          : dia.delMes
            ? dia.esPasado
              ? 'text-neutral-400'
              : 'font-medium text-brand-black'
            : 'text-neutral-300'
      }`}
    >
      {dia.fecha.getDate()}
    </span>
  );

  return (
    <div
      onMouseEnter={calcularSugerida}
      onDragOver={(e) => {
        if (!aceptaSoltar || !e.dataTransfer.types.includes(ARRASTRE)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!encima) setEncima(true);
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={(e) => {
        setEncima(false);
        const id = e.dataTransfer.getData(ARRASTRE);
        if (id) {
          e.preventDefault();
          onSoltar(id);
        }
      }}
      className={`group relative min-h-[128px] border-b border-r border-neutral-100 p-1.5 ${
        encima
          ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.07)] ring-2 ring-inset ring-brand-primary'
          : !dia.delMes
            ? 'bg-neutral-50'
            : finDeSemana
              ? 'bg-[#FAFAFB]'
              : 'bg-white'
      }`}
    >
      <>
        <div className="flex items-center justify-between">
          {numero}
          {/* Agregar sólo hacia adelante: un post en el pasado no lo publica nadie. */}
          {!dia.esPasado && dia.posts.length > 0 && (
            <button
              type="button"
              onClick={onNuevo}
              aria-label="Nuevo post este día"
              className="grid h-6 w-6 place-items-center rounded-lg text-neutral-400 opacity-0 hover:bg-neutral-100 hover:text-brand-black focus:opacity-100 group-hover:opacity-100"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="mt-1 space-y-1">
          {visibles.map((p) => (
            <Pildora key={p.id} post={p} delAgente={delAgente.has(p.id)} onAbrir={() => onAbrir(p.id)} />
          ))}
          {resto > 0 && (
            <button
              type="button"
              onClick={() => setDesplegado(true)}
              className="w-full rounded-lg py-0.5 px-1 text-left text-[11px] font-medium text-neutral-500 hover:bg-neutral-100"
            >
              +{resto} más
            </button>
          )}
          {desplegado && dia.posts.length > POSTS_VISIBLES && (
            <button
              type="button"
              onClick={() => setDesplegado(false)}
              className="w-full rounded-lg py-0.5 px-1 text-left text-[11px] text-neutral-500 hover:bg-neutral-100"
            >
              Ver menos
            </button>
          )}
          {/* Un día libre que viene: el hueco sugerido aparece al pasar el mouse. */}
          {!dia.esPasado && dia.posts.length === 0 && (
            <button
              type="button"
              onClick={onNuevo}
              className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.38)] py-2 text-[11px] font-medium text-brand-primary opacity-0 transition-opacity hover:bg-[rgb(var(--brand-primary-rgb)_/_0.07)] focus:opacity-100 group-hover:opacity-100"
            >
              <Plus className="w-3 h-3" />
              {sugerida ?? 'Post'}
            </button>
          )}
        </div>
      </>
    </div>
  );
}

/** El mes como agenda, agrupado por día. Es la vista por defecto en el teléfono. */
function ListaDelMes({
  posts,
  delAgente,
  onAbrir,
  onNuevo,
}: {
  posts: PostConMiniaturas[];
  delAgente: Set<string>;
  onAbrir: (id: string) => void;
  onNuevo: () => void;
}) {
  const dias = useMemo(() => {
    const grupos = new Map<string, PostConMiniaturas[]>();
    for (const p of posts) {
      const k = p.cuando!.toDateString();
      grupos.set(k, [...(grupos.get(k) ?? []), p]);
    }
    return [...grupos.values()];
  }, [posts]);

  if (dias.length === 0) {
    return (
      <div className="mt-3 rounded-2xl border border-dashed border-neutral-300 bg-white py-14 px-6 text-center">
        <CalendarDays className="mx-auto w-8 h-8 text-neutral-300" />
        <p className="mt-3 text-sm font-medium text-brand-black">No hay posts este mes</p>
        <p className="mx-auto mt-1 max-w-xs text-sm text-neutral-500">
          Pedíselo al asistente de arriba, o armá el primero a mano.
        </p>
        <button type="button" onClick={onNuevo} className={boton('secundario', { extra: 'mt-4' })}>
          <Plus className="w-4 h-4" /> Nuevo post
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4">
      {dias.map((grupo) => {
        const d = grupo[0]!.cuando!;
        const esHoy = d.toDateString() === new Date().toDateString();
        return (
          <section key={d.toDateString()}>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              <span
                className={`grid h-7 w-7 place-items-center rounded-full text-xs ${
                  esHoy ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-brand-black'
                }`}
              >
                {d.getDate()}
              </span>
              {d.toLocaleDateString('es-AR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </h3>
            <div className="space-y-2">
              {grupo.map((p) => {
                const tono = tonoDe(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onAbrir(p.id)}
                    className="flex w-full items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-2.5 text-left shadow-sm transition-colors hover:border-[rgb(var(--brand-primary-rgb)_/_0.225)]"
                  >
                    <span className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl bg-neutral-100">
                      <Miniatura post={p} icono="w-4 h-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-black">
                        {horaDe(p)}
                        <IconoDeFormato post={p} className="w-3.5 h-3.5 text-neutral-400" />
                        {delAgente.has(p.id) && <Sparkles className="w-3.5 h-3.5 text-brand-primary" />}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-neutral-500">
                        {p.texto.trim() || 'Sin descripción todavía'}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 rounded-full py-1 px-2.5 text-[11px] font-medium ${TONOS[tono].chip}`}
                    >
                      {TONOS[tono].texto}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
